import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Button,
    Card,
    Image,
    Input,
    Popconfirm,
    Space,
    Table,
    Tag,
    Tooltip,
    Typography,
    message,
} from 'antd';
import {
    RollbackOutlined,
    ScanOutlined,
    SearchOutlined,
} from '@ant-design/icons';
import { axiosInstance } from '../config';
import { promoProfitLoss } from '../components/Cost/costCalc';
import './OzonActions.css';

const LOSS_CELL = { background: '#fff1f2' };
const LOSS_HEADER = { background: '#ffe4e6', color: '#9f1239', fontWeight: 600 };

const moneyFormat = new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: 'RUB',
    maximumFractionDigits: 2,
});

const formatMoney = (value) => {
    const raw = value && typeof value === 'object' ? value.value ?? value.amount : value;
    if (raw === null || raw === undefined || raw === '') return '—';
    const number = Number(raw);
    return Number.isNaN(number) ? String(raw) : moneyFormat.format(number);
};

const productImageSrc = (value) =>
    typeof value === 'string' && /^https?:\/\//i.test(value) ? value : '';

const signedPercent = (value) => {
    if (value == null || Number.isNaN(Number(value))) return '—';
    const number = Number(value);
    const text = `${Math.abs(number).toFixed(1).replace('.', ',')}%`;
    if (number > 0) return `+${text}`;
    if (number < 0) return `−${text}`;
    return text;
};

const lossTone = (value) =>
    value < 0 ? 'text-rose-700' : value > 0 ? 'text-emerald-700' : 'text-slate-500';

const formatScanDate = (value) => {
    if (!value) return '';
    const date = new Date(value);
    return Number.isNaN(date.getTime())
        ? String(value)
        : date.toLocaleString('ru-RU', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
          });
};

const productKey = (record) => String(record?.product_id || record?.offer_id || '');

const applyScanPayload = (data, setters) => {
    const products = data?.products || [];
    setters.setProducts(products);
    setters.setSummary({
        scanned: data?.scanned || 0,
        with_base: data?.with_base || 0,
        without_base: data?.without_base || 0,
        total: data?.total ?? products.length,
    });
    setters.setScannedAt(data?.scanned_at || '');
    setters.setScanned(Boolean(data?.scanned_at) || products.length > 0 || (data?.scanned || 0) > 0);
};

const OzonPrices = () => {
    const [products, setProducts] = useState([]);
    const [summary, setSummary] = useState({
        scanned: 0,
        with_base: 0,
        without_base: 0,
        total: 0,
    });
    const [scanned, setScanned] = useState(false);
    const [scannedAt, setScannedAt] = useState('');
    const [loading, setLoading] = useState(false);
    const [loadingSaved, setLoadingSaved] = useState(true);
    const [query, setQuery] = useState('');
    const [resettingKey, setResettingKey] = useState('');
    const [resettingBatch, setResettingBatch] = useState(false);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [costItems, setCostItems] = useState([]);
    const [costCatalog, setCostCatalog] = useState([]);

    const scanSetters = { setProducts, setSummary, setScannedAt, setScanned };

    const loadCost = useCallback(async () => {
        try {
            const [itemsRes, catalogRes] = await Promise.all([
                axiosInstance.get('/v1/cost/items'),
                axiosInstance.get('/v1/cost/catalog'),
            ]);
            setCostItems(itemsRes.data || []);
            setCostCatalog(catalogRes.data || []);
        } catch (error) {
            message.error(
                error.response?.data?.detail || 'Не удалось загрузить расчёт себестоимости'
            );
        }
    }, []);

    const fillActions = async () => {
        const { data } = await axiosInstance.post('/v1/actions/ozon/prices/scan/actions');
        applyScanPayload(data, scanSetters);
        return data;
    };

    useEffect(() => {
        let cancelled = false;
        const loadSaved = async () => {
            try {
                const { data } = await axiosInstance.get('/v1/actions/ozon/prices/scan');
                if (cancelled) return;
                applyScanPayload(data, scanSetters);
                if (!cancelled && (data.products || []).length) {
                    await fillActions();
                }
            } catch (error) {
                if (!cancelled) {
                    message.error(
                        error.response?.data?.detail || 'Не удалось загрузить сохранённое сканирование'
                    );
                }
            } finally {
                if (!cancelled) setLoadingSaved(false);
            }
        };
        loadSaved();
        loadCost();
        return () => {
            cancelled = true;
        };
    }, [loadCost]);

    const scanPrices = async () => {
        setLoading(true);
        try {
            const { data } = await axiosInstance.post('/v1/actions/ozon/prices/scan');
            applyScanPayload(data, scanSetters);
            setSelectedRowKeys([]);
            if ((data.products || []).length) {
                await fillActions();
            }
            message.success(
                data.total
                    ? `Найдено расхождений: ${data.total}`
                    : 'Все цены совпадают с базовой'
            );
        } catch (error) {
            message.error(error.response?.data?.detail || 'Не удалось просканировать цены');
        } finally {
            setLoading(false);
        }
    };

    const dropResetProducts = (keys) => {
        const removed = new Set(keys.map(String));
        setProducts((current) => current.filter((item) => !removed.has(productKey(item))));
        setSelectedRowKeys((current) => current.filter((key) => !removed.has(String(key))));
        setSummary((current) => ({
            ...current,
            total: Math.max(0, (current.total || 0) - removed.size),
        }));
    };

    const resetPrice = async (record) => {
        const key = productKey(record);
        setResettingKey(key);
        try {
            const { data } = await axiosInstance.post('/v1/actions/ozon/prices/reset', {
                product_id: record.product_id || null,
                offer_id: record.offer_id || '',
            });
            message.success(
                `Цена ${data.offer_id || record.offer_id || ''} установлена: ${formatMoney(
                    data.price
                )}`
            );
            dropResetProducts([key]);
        } catch (error) {
            message.error(error.response?.data?.detail || 'Не удалось вернуть исходную цену');
        } finally {
            setResettingKey('');
        }
    };

    const resetSelectedPrices = async () => {
        const selected = products.filter((item) => selectedRowKeys.includes(productKey(item)));
        if (!selected.length) return;
        setResettingBatch(true);
        const succeeded = [];
        const failed = [];
        try {
            for (const record of selected) {
                try {
                    await axiosInstance.post('/v1/actions/ozon/prices/reset', {
                        product_id: record.product_id || null,
                        offer_id: record.offer_id || '',
                    });
                    succeeded.push(productKey(record));
                } catch (error) {
                    failed.push({
                        key: productKey(record),
                        name: record.offer_id || record.name || productKey(record),
                        error: error.response?.data?.detail || 'не удалось вернуть цену',
                    });
                }
            }
            if (succeeded.length) {
                dropResetProducts(succeeded);
            }
            setSelectedRowKeys(failed.map((item) => item.key));
            if (failed.length && succeeded.length) {
                message.warning(
                    `Вернули цены у ${succeeded.length}, не удалось: ${failed
                        .map((item) => item.name)
                        .join(', ')}`
                );
            } else if (failed.length) {
                message.error(
                    `Не удалось вернуть цены: ${failed.map((item) => item.name).join(', ')}`
                );
            } else {
                message.success(`Исходные цены установлены: ${succeeded.length}`);
            }
        } finally {
            setResettingBatch(false);
        }
    };

    const busy = Boolean(resettingKey) || resettingBatch;

    const selectedRecords = useMemo(
        () => products.filter((item) => selectedRowKeys.includes(productKey(item))),
        [products, selectedRowKeys]
    );

    const filtered = useMemo(() => {
        const text = query.trim().toLowerCase();
        if (!text) return products;
        return products.filter((item) =>
            [
                item.offer_id,
                item.name,
                item.product_id,
                item.cost_fragment,
                ...(item.actions || []).map((action) => action.title),
                item.in_action ? 'участвует' : 'не участвует',
            ]
                .filter((value) => value !== null && value !== undefined)
                .some((value) => String(value).toLowerCase().includes(text))
        );
    }, [products, query]);

    const columns = [
        {
            title: 'Товар',
            key: 'product',
            width: 360,
            onCell: () => ({ className: 'ozon-actions-product-cell' }),
            render: (_, record) => {
                const image = productImageSrc(record.primary_image);
                return (
                    <div className="ozon-actions-product">
                        {image ? (
                            <Image
                                src={image}
                                width={44}
                                height={44}
                                className="rounded object-cover"
                                preview
                            />
                        ) : null}
                        <div className="ozon-actions-product__text">
                            <Typography.Text strong className="ozon-actions-product__name">
                                {record.name || 'Без названия'}
                            </Typography.Text>
                            <Typography.Text type="secondary" copyable={Boolean(record.offer_id)}>
                                {record.offer_id || '—'}
                            </Typography.Text>
                        </div>
                    </div>
                );
            },
        },
        {
            title: 'ID',
            dataIndex: 'product_id',
            key: 'product_id',
            width: 120,
            render: (value) => value || '—',
        },
        {
            title: 'Базовая цена',
            dataIndex: 'seller_price',
            key: 'seller_price',
            width: 150,
            render: (value, record) => (
                <div>
                    <div className="font-semibold text-orange-800">{formatMoney(value)}</div>
                    {record.cost_fragment ? (
                        <div className="text-xs text-slate-400">{record.cost_fragment}</div>
                    ) : null}
                </div>
            ),
        },
        {
            title: 'Цена в Ozon',
            dataIndex: 'ozon_price',
            key: 'ozon_price',
            width: 150,
            render: formatMoney,
        },
        {
            title: 'Акции',
            key: 'actions_list',
            width: 260,
            render: (_, record) => {
                const actions = record.actions || [];
                if (!actions.length) {
                    return <Tag>Не участвует</Tag>;
                }
                return (
                    <div className="flex flex-col gap-1">
                        {actions.map((action) => (
                            <Tag key={action.id || action.title} color="green">
                                {action.title || `Акция ${action.id}`}
                            </Tag>
                        ))}
                    </div>
                );
            },
        },
        {
            title: 'Потеря маржи',
            key: 'margin_loss',
            width: 140,
            onHeaderCell: () => ({ style: LOSS_HEADER }),
            onCell: () => ({ style: LOSS_CELL }),
            render: (_, record) => {
                const loss = promoProfitLoss(
                    { ...record, action_price: record.action_price || record.ozon_price },
                    costItems,
                    costCatalog
                );
                const text = signedPercent(loss?.marginLossPercent);
                if (text === '—') return '—';
                return (
                    <span className={`text-sm font-semibold ${lossTone(loss.marginLossPercent)}`}>
                        {text}
                    </span>
                );
            },
        },
        {
            title: 'Разница',
            key: 'diff',
            width: 160,
            render: (_, record) => {
                const value = record.diff?.value ?? record.diff;
                return (
                    <div className={`font-semibold ${lossTone(value)}`}>
                        <div>{formatMoney(record.diff)}</div>
                        <div className="text-xs">{signedPercent(record.diff_percent)}</div>
                    </div>
                );
            },
        },
        {
            title: '',
            key: 'actions',
            width: 220,
            render: (_, record) => {
                const key = productKey(record);
                const disabled = !record.seller_price || busy;
                const button = (
                    <Button
                        type="link"
                        icon={<RollbackOutlined />}
                        loading={resettingKey === key}
                        disabled={disabled}
                    >
                        Вернуть исходную цену
                    </Button>
                );
                if (!record.seller_price) {
                    return (
                        <Tooltip title="Нет исходной цены в Себестоимости">
                            <span>{button}</span>
                        </Tooltip>
                    );
                }
                return (
                    <Popconfirm
                        title="Вернуть исходную цену?"
                        description={`Предельная цена станет равной ${formatMoney(
                            record.seller_price
                        )}`}
                        okText="Вернуть"
                        cancelText="Отмена"
                        disabled={disabled}
                        onConfirm={() => resetPrice(record)}
                    >
                        {button}
                    </Popconfirm>
                );
            },
        },
    ];

    return (
        <div className="w-full space-y-4">
            <Card>
                <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                        <Typography.Title level={2} style={{ margin: 0 }}>
                            Цены
                        </Typography.Title>
                        <Typography.Text type="secondary">
                            Товары, у которых цена в Ozon отличается от цены продавца в себестоимости
                            {scannedAt ? `. Сканирование от ${formatScanDate(scannedAt)}` : ''}
                        </Typography.Text>
                    </div>
                    <Button type="primary" icon={<ScanOutlined />} loading={loading} onClick={scanPrices}>
                        Сканировать
                    </Button>
                </div>

                {scanned ? (
                    <div className="mt-5 grid gap-3 sm:grid-cols-4">
                        <div className="rounded-xl bg-slate-50 px-4 py-3">
                            <div className="text-xs text-slate-500">Проверено</div>
                            <div className="text-xl font-semibold">{summary.scanned}</div>
                        </div>
                        <div className="rounded-xl bg-slate-50 px-4 py-3">
                            <div className="text-xs text-slate-500">С базовой ценой</div>
                            <div className="text-xl font-semibold">{summary.with_base}</div>
                        </div>
                        <div className="rounded-xl bg-slate-50 px-4 py-3">
                            <div className="text-xs text-slate-500">Без базовой цены</div>
                            <div className="text-xl font-semibold">{summary.without_base}</div>
                        </div>
                        <div className="rounded-xl bg-orange-50 px-4 py-3">
                            <div className="text-xs text-orange-700">Расхождений</div>
                            <div className="text-xl font-semibold text-orange-800">{summary.total}</div>
                        </div>
                    </div>
                ) : null}

                <div className="mt-5">
                    <Space wrap className="mb-3">
                        <Input
                            allowClear
                            prefix={<SearchOutlined />}
                            placeholder="Артикул, название или ID"
                            value={query}
                            onChange={(event) => setQuery(event.target.value)}
                            style={{ minWidth: 280 }}
                        />
                    </Space>
                    <div className="ozon-actions-table-wrap">
                        <Table
                            rowKey={productKey}
                            columns={columns}
                            dataSource={filtered}
                            loading={loading || loadingSaved}
                            size="small"
                            scroll={{ x: 1340 }}
                            rowSelection={{
                                selectedRowKeys,
                                onChange: (keys) => setSelectedRowKeys(keys.map(String)),
                                getCheckboxProps: (record) => ({
                                    disabled: !record.seller_price || busy,
                                }),
                            }}
                            locale={{
                                emptyText: scanned
                                    ? 'Расхождений нет'
                                    : 'Нажмите «Сканировать», чтобы найти отличия',
                            }}
                            pagination={{
                                pageSize: 20,
                                showSizeChanger: true,
                                pageSizeOptions: [20, 50, 100],
                                showTotal: (total) => `Товаров: ${total}`,
                            }}
                        />
                    </div>
                    {selectedRecords.length ? (
                        <div className="ozon-prices-bulk-bar">
                            <Typography.Text>
                                Выбрано товаров: {selectedRecords.length}
                            </Typography.Text>
                            <Popconfirm
                                title="Вернуть исходные цены?"
                                description={`Предельная цена станет равной цене продавца у ${selectedRecords.length} товаров`}
                                okText="Вернуть"
                                cancelText="Отмена"
                                disabled={busy}
                                onConfirm={resetSelectedPrices}
                            >
                                <Button
                                    type="primary"
                                    icon={<RollbackOutlined />}
                                    loading={resettingBatch}
                                    disabled={busy && !resettingBatch}
                                >
                                    Вернуть исходные цены
                                </Button>
                            </Popconfirm>
                        </div>
                    ) : null}
                </div>
            </Card>
        </div>
    );
};

export default OzonPrices;
